import React from 'react';
import { CardTW } from '../../../../tailwindComponents/CardTW/CardTW';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { Show } from '../../../shared/Show';
import { FlexRow } from '../../../../ui-toolkit';
import { usePatchAttributesMutation } from '../../../../API/order/attributes/AttributesApi';
import { UUID } from '../../../../types/shared/UUID';
import { ButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { SimpleGrid } from '../../../../tailwindComponents/grid/SimpleGrid';
import { LabelTextTW } from '../../../../tailwindComponents/formCellTW/LabelTextTW';
import { ErrorMessageTW } from '../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { SpinnerTW } from '../../../../tailwindComponents/spinnerTW/SpinnerTW';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { updatePropertyTitleScheme } from '../../../../validation/order/OrderAttributesValidator';
import { Form, Formik } from 'formik';
import { TextFieldTW } from '../../../../tailwindComponents/textInputTW/TextFieldTW';
import { IOrderAttributesData } from '../../../../types/order/attributes/IOrderAttributesData';
import { IPropertyTitleData } from '../../../../types/order/attributes/IPropertyTitleData';
import { OutsideClickHandler } from 'components/shared/InteractionManagement/OutsideClickHandler';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';
import { notEmpty } from '../../../../ui-toolkit/utilities/notEmpty';

export interface IPropertyTitleSectionProps {
  orderId: UUID;
  attributes?: IOrderAttributesData;
  isCollapsed: boolean;
  setIsCollapsed: (newValue: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (newValue: boolean) => void;
}

const hasBeenUpdated = (propertyTitle?: IPropertyTitleData): boolean => {
  return (
    getDateString(DateFormat.short, propertyTitle?.createdAt) !==
      getDateString(DateFormat.short, propertyTitle?.updatedAt) ||
    getDateString(DateFormat.hugeTime, propertyTitle?.createdAt) !==
      getDateString(DateFormat.hugeTime, propertyTitle?.updatedAt)
  );
};

export const PropertyTitleSection: React.FC<IPropertyTitleSectionProps> = ({
  orderId,
  attributes,
  isCollapsed,
  setIsCollapsed,
  isEditMode,
  setIsEditMode,
}) => {
  const [patchAttributes, { error, isLoading }] = usePatchAttributesMutation();

  const toggleEditMode = (isEditMode: boolean) => setIsEditMode(isEditMode);

  const initialValues = {
    apn: attributes?.propertyTitle?.apn,
  };

  const saveUpdateAttributes = async (values: IPropertyTitleData) => {
    const propertyTitleData: IPropertyTitleData = {
      apn: values.apn !== initialValues.apn ? values.apn : undefined,
    };
    const body = {} as IOrderAttributesData;
    if (Object.values(propertyTitleData).some((value) => notEmpty(value))) {
      body.propertyTitle = propertyTitleData;
    }
    const result = await patchAttributes({ orderId, body });
    if (checkForMutationSuccess(result, 'APN Updated', 'Error Updating APN')) {
      toggleEditMode(false);
    }
  };

  return (
    <>
      <ErrorMessageTW rtkError={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={saveUpdateAttributes}
        validationSchema={updatePropertyTitleScheme}
        validateOnChange
        enableReinitialize
      >
        {({ submitForm, resetForm }) => (
          <Form>
            <OutsideClickHandler
              enabled={isEditMode}
              onCloseAndSave={() => submitForm()}
              onClose={() => toggleEditMode(false)}
            >
              <CardTW
                title={
                  <div className={'flex flex-row items-center'}>
                    <Show when={isEditMode}>
                      <ChevronDownIcon className="h-7 w-7 flex-none ease-in-out" />
                    </Show>
                    Property & Title Info
                  </div>
                }
                titleActions={
                  <Show when={!isLoading} fallback={<SpinnerTW />}>
                    <Show when={!isCollapsed && !isEditMode}>
                      <ButtonTW
                        ghost
                        secondary
                        className="!-ml-6"
                        data-test-id="property-title-edit-mode-button"
                        onClick={() => toggleEditMode(true)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </ButtonTW>
                    </Show>
                    <Show when={!isCollapsed && isEditMode}>
                      <div className="flex items-center">
                        <ButtonTW type="submit" secondary>
                          Save
                        </ButtonTW>
                        <ButtonTW
                          type="button"
                          ghost
                          secondary
                          className="px-2 font-normal text-foundation-primary hover:underline"
                          onClick={() => {
                            resetForm();
                            toggleEditMode(false);
                          }}
                        >
                          Cancel
                        </ButtonTW>
                      </div>
                    </Show>
                  </Show>
                }
                isCollapsible={!isEditMode}
                isCollapsed={isCollapsed}
                onChangeCollapseState={() => setIsCollapsed(!isCollapsed)}
                titleClassName={'text-xl'}
                key={`property-title-card-${isCollapsed}`}
              >
                <SimpleGrid baseline>
                  <Show
                    when={isEditMode}
                    fallback={
                      <>
                        <SimpleGrid columnCount={4} baseline>
                          <div>
                            <FlexRow className="font-medium">Assessor's parcel number/tax id</FlexRow>
                            <LabelTextTW labelText={attributes?.propertyTitle?.apn ?? ''} />
                          </div>
                        </SimpleGrid>

                        <Show when={hasBeenUpdated(attributes?.propertyTitle)}>
                          <FlexRow className="text-sm text-gray-500">
                            Last updated on
                            {getDateString(DateFormat.shortDateTimeAt, `${attributes?.propertyTitle?.updatedAt}`)}
                          </FlexRow>
                        </Show>
                      </>
                    }
                  >
                    <SimpleGrid columnCount={4} baseline>
                      <TextFieldTW name="apn" labelText={"Assessor's parcel number/tax id"} />
                    </SimpleGrid>
                  </Show>
                </SimpleGrid>
              </CardTW>
            </OutsideClickHandler>
          </Form>
        )}
      </Formik>
    </>
  );
};
